import { MouseEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Slider } from "../ui/Slider"
import { ColorSwatches, SwatchesList } from "../Swatches"
import { ColorWheel } from "./ColorWheel"
import { useEditor } from "@/utils/useEditor"
import { Adjustments } from "@/core/types"

const GradingRangeColors = ["black", "gray", "white", "multigray"] as const
type GradingRangeColor = (typeof GradingRangeColors)[number]

const GradingRanges = ["Shadows", "Midtones", "Highlights", "Global"] as const
type GradingRange = (typeof GradingRanges)[number]

const gradingRangeKeys: Record<GradingRange, string> = {
	Shadows: "grading.rangeShadows",
	Midtones: "grading.rangeMidtones",
	Highlights: "grading.rangeHighlights",
	Global: "grading.rangeGlobal",
}

const GradingAdjustmentNames = ["Hue", "Saturation", "Luminance"] as const

const GradingRangeMap: { [key in GradingRangeColor]: GradingRange } = {
	black: "Shadows",
	gray: "Midtones",
	white: "Highlights",
	multigray: "Global",
}

const GradingAdjustments = GradingRanges.flatMap((range) => {
	return GradingAdjustmentNames.map((adjustment) => {
		return `grading${range}${adjustment}`
	})
})

const SelectGradingAdjustments = (values: Adjustments) => {
	return GradingAdjustments.reduce((all, curr) => {
		all[curr] = values[curr]
		return all
	}, {})
}

export const Grading = (props: {
	values: Adjustments
	saveChange: (name: string, val?: number) => void
}) => {
	const editor = useEditor()
	const { t } = useTranslation("components")

	const [allGradingAdjustments, setAllGradingAdjustments] = useState(
		SelectGradingAdjustments(props.values),
	)

	const [selectedRangeName, setSelectedRangeName] = useState<GradingRangeColor>(
		GradingRangeColors[0],
	)

	useEffect(() => {
		setAllGradingAdjustments(
			GradingAdjustments.reduce((all, curr) => {
				all[curr] = props.values[curr]
				return all
			}, {}),
		)
	}, [props.values])

	/* Actions */

	const updateSelectedRangeName = (event: MouseEvent<HTMLDivElement>, color: ColorSwatches) => {
		setSelectedRangeName(color as GradingRangeColor)

		event.preventDefault()

		const range = GradingRangeMap[color]
		document.querySelector(`[data-key=grading${range}]`)?.scrollIntoView({
			behavior: "smooth",
			block: "nearest",
			inline: "center",
		})
	}

	/* Rendering */

	const renderCommonSliders = () => {
		return (
			<>
				<Slider
					defaultValue={50}
					min={0}
					max={100}
					title={t("grading.sliderBlending")}
					panelName={t("grading.panelColorGrading")}
					focusKey="gradingBlending"
					keyboardStep={1}
					shiftKeyboardStep={10}
					hoverKeyboardStep={5}
					hoverShiftKeyboardStep={20}
					value={props.values[`gradingBlending`]}
					onUpdate={(val) => editor.setAdjustments({ [`gradingBlending`]: val })}
					onUpdateEnded={(_, displayedValue) =>
						props.saveChange(`Color Grading Blending`, displayedValue)
					}
				/>
				<Slider
					title={t("grading.sliderBalance")}
					panelName={t("grading.panelColorGrading")}
					focusKey="gradingBalance"
					keyboardStep={1}
					shiftKeyboardStep={10}
					hoverKeyboardStep={5}
					hoverShiftKeyboardStep={20}
					value={props.values[`gradingBalance`]}
					onUpdate={(val) => editor.setAdjustments({ [`gradingBalance`]: val })}
					onUpdateEnded={(_, displayedValue) =>
						props.saveChange(`Color Grading Balance`, displayedValue)
					}
				/>
			</>
		)
	}

	const renderRange = (range: GradingRange) => {
		const hueName = `grading${range}Hue`
		const saturationName = `grading${range}Saturation`

		const updateHueAndSaturation = (hue: number, saturation: number) => {
			const newAdjustments = {
				[hueName]: hue,
				[saturationName]: saturation,
			}

			setAllGradingAdjustments({ ...allGradingAdjustments, ...newAdjustments })

			editor.setAdjustments({ ...newAdjustments })
		}

		const hue = allGradingAdjustments[hueName]
		const saturation = allGradingAdjustments[saturationName]

		return (
			<div
				key={`grading${range}`}
				data-key={`grading${range}`}
				className="relative w-full shrink-0 snap-center"
			>
				<ColorWheel
					onChange={updateHueAndSaturation}
					className="mx-auto my-4 size-36"
					hue={hue}
					saturation={saturation}
					saveChange={() => {
						props.saveChange(`${range} Hue`)
					}}
				/>
				<Slider
					title={t("grading.sliderHue")}
					panelName={t("grading.panelColorGrading")}
					sectionName={t("grading.hue", { range: range })}
					min={0}
					max={360}
					focusKey={`grading${range}Hue`}
					keyboardStep={1}
					shiftKeyboardStep={10}
					hoverKeyboardStep={5}
					hoverShiftKeyboardStep={20}
					value={props.values[`grading${range}Hue`]}
					transform={{
						toRange(val) {
							return Math.round(val * 360)
						},
						fromRange(val) {
							return val / 360
						},
					}}
					onUpdate={(val) => editor.setAdjustments({ [`grading${range}Hue`]: val })}
					onUpdateEnded={(_, displayedValue) =>
						props.saveChange(`${range} Hue`, displayedValue)
					}
				/>
				<Slider
					title={t("grading.sliderSaturation")}
					panelName={t("grading.panelColorGrading")}
					sectionName={t("grading.saturation", { range: range })}
					min={0}
					focusKey={`grading${range}Saturation`}
					keyboardStep={1}
					shiftKeyboardStep={10}
					hoverKeyboardStep={5}
					hoverShiftKeyboardStep={20}
					value={props.values[`grading${range}Saturation`]}
					onUpdate={(val) =>
						editor.setAdjustments({ [`grading${range}Saturation`]: val })
					}
					onUpdateEnded={(_, displayedValue) =>
						props.saveChange(`${range} Saturation`, displayedValue)
					}
				/>
				<Slider
					title={t("grading.sliderLuminance")}
					panelName={t("grading.panelColorGrading")}
					sectionName={t("grading.luminance", { range: range })}
					focusKey={`grading${range}Luminance`}
					keyboardStep={1}
					shiftKeyboardStep={10}
					hoverKeyboardStep={5}
					hoverShiftKeyboardStep={20}
					value={props.values[`grading${range}Luminance`]}
					onUpdate={(val) => editor.setAdjustments({ [`grading${range}Luminance`]: val })}
					onUpdateEnded={(_, displayedValue) =>
						props.saveChange(`${range} Luminance`, displayedValue)
					}
				/>
				<div className="absolute right-0 top-0 flex flex-col text-right text-xs">
					<label>
						H <span>{Math.round(hue * 360)}</span>
					</label>
					<label>
						S <span>{Math.round(saturation * 100)}</span>
					</label>
				</div>
				<span className="absolute left-0 top-0 text-xs">{t(gradingRangeKeys[range])}</span>
			</div>
		)
	}

	return (
		<div className="box-border flex w-full select-none flex-col">
			<div className="flex w-full items-center justify-between overflow-hidden">
				<SwatchesList
					onChange={updateSelectedRangeName}
					selected={selectedRangeName}
					compact={true}
					colors={GradingRangeColors as unknown as ColorSwatches[]}
				/>
			</div>
			<div className="mt-1 flex w-full snap-x snap-mandatory overflow-hidden">
				{GradingRanges.map((range) => renderRange(range))}
			</div>
			<div className="relative mt-3 w-full">{renderCommonSliders()}</div>
		</div>
	)
}
